import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Facebook, Instagram, Twitter, Youtube, Linkedin, Sun, Moon } from 'lucide-react';
import { Button } from './ui/button';
import { useTheme } from '../contexts/ThemeContext';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [settings, setSettings] = useState(null);
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Fetch settings for logo
    fetch(`${BACKEND_URL}/api/settings/`)
      .then(res => res.json())
      .then(data => setSettings(data))
      .catch(err => console.error('Error fetching settings:', err));
  }, [BACKEND_URL]);

  const navLinks = [
    { name: 'الرئيسية', path: '/' },
    { name: 'عن الجامعة', path: '/about' },
    { name: 'الكليات', path: '/colleges' },
    { name: 'البرامج الأكاديمية', path: '/programs' },
    { name: 'الإدارات والمراكز', path: '/departments' },
    { name: 'الأبحاث المنشورة', path: '/research' },
    { name: 'الأخبار والفعاليات', path: '/news' },
    { name: 'المعرض', path: '/gallery' },
    { name: 'تواصل معنا', path: '/contact' }
  ];

  const isHomePage = location.pathname === '/';

  return (
    <nav 
      className={`fixed w-full z-50 transition-all duration-300 ${
        scrolled 
          ? theme === 'dark' 
            ? 'bg-[#0A1929]/95 backdrop-blur-md shadow-lg border-b border-gray-800' 
            : 'bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-100'
          : isHomePage
            ? 'bg-gradient-to-b from-[#071738]/95 via-[#071738]/70 to-transparent backdrop-blur-[2px]'
            : 'bg-[#071738] shadow-md border-b border-white/10'
      }`}
      style={!scrolled && !isHomePage ? { backgroundColor: '#071738', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' } : undefined}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo Only */}
          <Link 
            to="/" 
            onClick={() => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })}
            className="flex items-center"
          >
            {settings?.site_logo ? (
              <img 
                src={settings.site_logo} 
                alt="شعار جامعة ابن النفيس للعلوم الطبية" 
                className="h-20 w-auto object-contain transition-all duration-300"
                style={{ maxWidth: '250px' }}
              />
            ) : (
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#0F388E] rounded-lg flex items-center justify-center">
                  <span className="text-[#C9A040] font-bold text-xl">ج</span>
                </div>
                <div>
                  <h1 className={`font-bold text-lg transition-colors ${
                    scrolled 
                      ? theme === 'dark' 
                        ? 'text-[#42A5F5]' 
                        : 'text-[#0F388E]'
                      : 'text-white'
                  }`}>
                    جامعة ابن النفيس
                  </h1>
                  <p className={`text-xs transition-colors ${
                    scrolled 
                      ? theme === 'dark' 
                        ? 'text-[#FFD54F]' 
                        : 'text-[#C9A040]'
                      : 'text-white/90'
                  }`}>
                    للعلوم الطبية
                  </p>
                </div>
              </div>
            )}
          </Link>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })}
                className={`text-sm font-medium transition-colors hover:text-[#C9A040] ${
                  location.pathname === link.path
                    ? 'text-[#C9A040]'
                    : scrolled 
                      ? theme === 'dark' 
                        ? 'text-[#E3F2FD]' 
                        : 'text-[#0F388E]'
                      : 'text-white'
                }`}
              >
                {link.name}
              </Link>
            ))}
            
            {/* Social Media Icons */}
            {settings?.contact_settings && (
              <div className="flex items-center gap-3 border-r border-white/20 pr-6">
                <span className={`text-xs font-medium ${
                  scrolled 
                    ? theme === 'dark' 
                      ? 'text-[#E3F2FD]' 
                      : 'text-[#0F388E]'
                    : 'text-white/80'
                }`}>
                  تابعنا:
                </span>
                {settings.contact_settings.facebook && (
                  <a 
                    href={settings.contact_settings.facebook} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={`transition-colors hover:text-[#C9A040] ${
                      scrolled 
                        ? theme === 'dark' 
                          ? 'text-[#E3F2FD]' 
                          : 'text-[#0F388E]'
                        : 'text-white'
                    }`}
                    aria-label="Facebook"
                  >
                    <Facebook className="w-4 h-4" />
                  </a>
                )}
                {settings.contact_settings.instagram && (
                  <a 
                    href={settings.contact_settings.instagram} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={`transition-colors hover:text-[#C9A040] ${
                      scrolled 
                        ? theme === 'dark' 
                          ? 'text-[#E3F2FD]' 
                          : 'text-[#0F388E]' 
                        : 'text-white'
                    }`}
                    aria-label="Instagram"
                  >
                    <Instagram className="w-4 h-4" />
                  </a>
                )}
                {settings.contact_settings.twitter && (
                  <a 
                    href={settings.contact_settings.twitter} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={`transition-colors hover:text-[#C9A040] ${
                      scrolled 
                        ? theme === 'dark' 
                          ? 'text-[#E3F2FD]' 
                          : 'text-[#0F388E]' 
                        : 'text-white'
                    }`}
                    aria-label="Twitter/X"
                  >
                    <Twitter className="w-4 h-4" />
                  </a>
                )}
                {settings.contact_settings.youtube && (
                  <a 
                    href={settings.contact_settings.youtube} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={`transition-colors hover:text-[#C9A040] ${
                      scrolled 
                        ? theme === 'dark' 
                          ? 'text-[#E3F2FD]' 
                          : 'text-[#0F388E]' 
                        : 'text-white'
                    }`}
                    aria-label="YouTube"
                  >
                    <Youtube className="w-4 h-4" />
                  </a>
                )}
                {settings.contact_settings.linkedin && (
                  <a 
                    href={settings.contact_settings.linkedin} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={`transition-colors hover:text-[#C9A040] ${
                      scrolled 
                        ? theme === 'dark' 
                          ? 'text-[#E3F2FD]' 
                          : 'text-[#0F388E]' 
                        : 'text-white'
                    }`}
                    aria-label="LinkedIn"
                  >
                    <Linkedin className="w-4 h-4" />
                  </a>
                )}
              </div>
            )}
            
            {/* Theme Toggle Button */}
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-lg transition-all hover:scale-110 ${
                scrolled 
                  ? theme === 'dark'
                    ? 'bg-[#132F4C] text-[#FFD54F] hover:bg-[#1a4066]'
                    : 'bg-gray-100 text-[#0F388E] hover:bg-gray-200'
                  : 'bg-white/10 text-white hover:bg-white/20'
              }`}
              aria-label="تبديل الوضع المظلم"
            >
              {theme === 'dark' ? (
                <Sun className="w-5 h-5" />
              ) : (
                <Moon className="w-5 h-5" />
              )}
            </button>
            
            <Link to="/register" onClick={() => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })}>
              <Button className="bg-[#C9A040] hover:bg-[#C9A040]/90 text-white">
                التسجيل
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center gap-3">
            {/* Theme Toggle Button - Mobile */}
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-lg transition-all ${
                scrolled 
                  ? theme === 'dark'
                    ? 'bg-[#132F4C] text-[#FFD54F]'
                    : 'bg-gray-100 text-[#0F388E]'
                  : 'bg-white/10 text-white'
              }`}
              aria-label="تبديل الوضع المظلم"
            >
              {theme === 'dark' ? (
                <Sun className="w-5 h-5" />
              ) : (
                <Moon className="w-5 h-5" />
              )}
            </button>
            
            <button
              onClick={() => setIsOpen(!isOpen)}
              aria-label="القائمة"
            >
              {isOpen ? (
                <X className={scrolled ? (theme === 'dark' ? 'text-white' : 'text-[#0F388E]') : 'text-white'} />
              ) : (
                <Menu className={scrolled ? (theme === 'dark' ? 'text-white' : 'text-[#0F388E]') : 'text-white'} />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className={`lg:hidden border-t ${
          theme === 'dark'
            ? 'bg-[#0A1929] border-gray-800'
            : 'bg-white border-gray-200'
        }`}>
          <div className="px-4 py-4 space-y-3">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => {
                  setIsOpen(false);
                  window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                }}
                className={`block py-2 text-sm font-medium transition-colors hover:text-[#C9A040] ${
                  location.pathname === link.path
                    ? 'text-[#C9A040]'
                    : theme === 'dark'
                      ? 'text-[#E3F2FD]'
                      : 'text-[#0F388E]'
                }`}
              >
                {link.name}
              </Link>
            ))}
            
            {/* Social Media Icons - Mobile */}
            {settings?.contact_settings && (
              <div className="border-t border-gray-200 dark:border-gray-800 pt-4 mt-4">
                <p className="text-xs font-medium text-[#0F388E] dark:text-[#C9A040] mb-3">تابعنا على:</p>
                <div className="flex items-center gap-4">
                  {settings.contact_settings.facebook && (
                    <a 
                      href={settings.contact_settings.facebook} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-full bg-[#0F388E]/10 dark:bg-white/10 hover:bg-[#C9A040] hover:text-white transition-all flex items-center justify-center text-[#0F388E] dark:text-white"
                      aria-label="Facebook"
                    >
                      <Facebook className="w-5 h-5" />
                    </a>
                  )}
                  {settings.contact_settings.instagram && (
                    <a 
                      href={settings.contact_settings.instagram} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-full bg-[#0F388E]/10 dark:bg-white/10 hover:bg-[#C9A040] hover:text-white transition-all flex items-center justify-center text-[#0F388E] dark:text-white"
                      aria-label="Instagram"
                    >
                      <Instagram className="w-5 h-5" />
                    </a>
                  )}
                  {settings.contact_settings.twitter && (
                    <a 
                      href={settings.contact_settings.twitter} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-full bg-[#0F388E]/10 dark:bg-white/10 hover:bg-[#C9A040] hover:text-white transition-all flex items-center justify-center text-[#0F388E] dark:text-white"
                      aria-label="Twitter/X"
                    >
                      <Twitter className="w-5 h-5" />
                    </a>
                  )}
                  {settings.contact_settings.youtube && (
                    <a 
                      href={settings.contact_settings.youtube} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-full bg-[#0F388E]/10 dark:bg-white/10 hover:bg-[#C9A040] hover:text-white transition-all flex items-center justify-center text-[#0F388E] dark:text-white"
                      aria-label="YouTube"
                    >
                      <Youtube className="w-5 h-5" />
                    </a>
                  )}
                  {settings.contact_settings.linkedin && (
                    <a 
                      href={settings.contact_settings.linkedin} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-full bg-[#0F388E]/10 dark:bg-white/10 hover:bg-[#C9A040] hover:text-white transition-all flex items-center justify-center text-[#0F388E] dark:text-white"
                      aria-label="LinkedIn"
                    >
                      <Linkedin className="w-5 h-5" />
                    </a>
                  )}
                </div>
              </div>
            )}
            
            <Link 
              to="/register" 
              onClick={() => {
                setIsOpen(false);
                window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
              }}
            >
              <Button className="w-full bg-[#C9A040] hover:bg-[#C9A040]/90 text-white mt-4">
                التسجيل
              </Button>
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
